import { useEffect, useState } from 'react'
import { Box, Flex, Text, VStack, Modal } from '@minetrace/ui'
import { LuExternalLink, LuMapPin, LuFileText, LuCalendar } from 'react-icons/lu'

import { restrictedZonesIntelService } from '../../../api/services/restrictedZonesIntel.service'
import {
  type RestrictedZoneDetail,
  intelRestrictionClassColor,
  intelRestrictionClassLabel,
  intelSourceLabel,
} from '../../../api/models/response/restrictedZoneIntel'

interface Props {
  zoneId: string
  onClose: () => void
}

const COLOR_HEX: Record<string, { bg: string; fg: string }> = {
  red: { bg: '#fae1db', fg: '#991b1b' },
  orange: { bg: '#ffedd5', fg: '#9a3412' },
  yellow: { bg: '#f9eccf', fg: '#854d0e' },
  amber: { bg: '#fde68a', fg: '#92400e' },
  gray: { bg: '#e8dfd1', fg: '#4f4842' },
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return iso ?? '—' }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('es-PE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return iso ?? '—' }
}

// Pretty-prints a JSON string. Returns null if it isn't valid JSON or is empty.
function tryFormatJson(s: string | null): string | null {
  if (!s) return null
  try { return JSON.stringify(JSON.parse(s), null, 2) }
  catch { return s }
}

export default function ZoneDetailModal({ zoneId, onClose }: Props) {
  const [data, setData] = useState<RestrictedZoneDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Estado inicial (loading=true, data=null) ya está seteado por useState
    // — el padre usa <ZoneDetailModal key={zoneId} /> para remount limpio
    // cuando cambia la zona seleccionada, así no necesitamos pre-setear
    // estado sincrónico aquí (que dispara el lint react-hooks/no-cascading).
    let cancelled = false
    restrictedZonesIntelService.getById(zoneId)
      .then((d) => { if (!cancelled) { setData(d); setLoading(false) } })
      .catch((e) => { if (!cancelled) { setError(e instanceof Error ? e.message : String(e)); setLoading(false) } })
    return () => { cancelled = true }
  }, [zoneId])

  const colorKey = data ? intelRestrictionClassColor(data.restrictionClass) : 'gray'
  const colors = COLOR_HEX[colorKey] ?? COLOR_HEX.gray
  const attrs = data ? tryFormatJson(data.attributesJson) : null

  return (
    <Modal
      open
      onClose={onClose}
      title={data?.name ?? 'Zona restringida'}
      subtitle={data ? `${intelSourceLabel(data.source)} · ${data.layer}` : 'Cargando…'}
      size="lg"
    >
      {loading && (
        <Box py={8} textAlign="center" color="#857b71">Cargando detalle…</Box>
      )}

      {error && !loading && (
        <Box bg="#fae1db" color="#991b1b" p={3} borderRadius="md" fontSize="sm">{error}</Box>
      )}

      {data && !loading && (
        <VStack align="stretch" gap={4}>
          {/* Class badge */}
          <Box
            display="inline-flex"
            alignSelf="flex-start"
            alignItems="center"
            gap={2}
            px={3}
            py={1.5}
            borderRadius="full"
            bg={colors.bg}
            color={colors.fg}
            fontSize="xs"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.04em"
          >
            {intelRestrictionClassLabel(data.restrictionClass)}
            {data.isReferential && <Text as="span" fontSize="10px">· REFERENCIAL</Text>}
          </Box>

          {/* Categoría */}
          {data.category && (
            <Box>
              <Text fontSize="11px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">Categoría</Text>
              <Text fontSize="sm">{data.category}</Text>
            </Box>
          )}

          {/* Norma legal */}
          {data.norma && (
            <Box>
              <Flex alignItems="center" gap={1.5}>
                <LuFileText size={12} color="#857b71" />
                <Text fontSize="11px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">Norma legal</Text>
              </Flex>
              <Text fontSize="sm">{data.norma}</Text>
            </Box>
          )}

          {/* Entidad */}
          {data.entidad && (
            <Box>
              <Text fontSize="11px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">Entidad</Text>
              <Text fontSize="sm">{data.entidad}</Text>
            </Box>
          )}

          {/* Fechas: publicación + sync */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Flex alignItems="center" gap={1.5}>
                <LuCalendar size={12} color="#857b71" />
                <Text fontSize="11px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">Publicación</Text>
              </Flex>
              <Text fontSize="sm">{formatDate(data.fechaPublicacion)}</Text>
            </Box>
            <Box>
              <Flex alignItems="center" gap={1.5}>
                <LuCalendar size={12} color="#857b71" />
                <Text fontSize="11px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">Sincronizado</Text>
              </Flex>
              <Text fontSize="sm">{formatDateTime(data.syncedAt)}</Text>
            </Box>
          </Box>

          {/* External ID */}
          <Box>
            <Text fontSize="11px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">ID externo (fuente)</Text>
            <Text fontSize="xs" fontFamily="monospace" color="#4f4842">{data.externalId}</Text>
          </Box>

          {/* Source link */}
          {data.sourceUrl && (
            <Box>
              <a
                href={data.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#b4532a', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
              >
                <LuExternalLink size={14} />
                Ver en fuente original
              </a>
            </Box>
          )}

          {/* Raw attributes (collapsible JSON) */}
          {attrs && (
            <Box>
              <Text fontSize="11px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em" mb={1}>
                <LuMapPin size={11} style={{ display: 'inline-block', marginRight: 4 }} />
                Atributos crudos (fuente)
              </Text>
              <Box bg="#faf6f0" border="1px solid #e8dfd1" borderRadius="md" p={2} maxH="200px" overflow="auto">
                <Box as="pre" fontSize="11px" fontFamily="monospace" color="#4f4842" m={0} whiteSpace="pre-wrap">
                  {attrs}
                </Box>
              </Box>
            </Box>
          )}
        </VStack>
      )}
    </Modal>
  )
}
